import { useEffect, useRef, useState, type ReactNode } from "react";
import { App } from "@capacitor/app";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { BrandLogo } from "../components/BrandLogo";
import { auth } from "../firebase/client";
import { useI18n } from "../i18n/I18nProvider";
import { isNative } from "../utils/platform";
import {
  LOCAL_LOCK_SETTINGS_EVENT,
  authenticateBiometricDetailed,
  biometricMessageKey,
  getLocalLockSettings,
  isBiometricPromptInFlight,
  verifyPin,
  type LocalLockMethod,
  type LocalLockSettings
} from "./localSecurity";

export function LocalLockGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [settings, setSettings] = useState<LocalLockSettings | null>(null);
  const settingsRef = useRef<LocalLockSettings | null>(null);
  const [checking, setChecking] = useState(true);
  const [locked, setLocked] = useState(false);
  const [unlockMethod, setUnlockMethod] = useState<LocalLockMethod | "">("");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [biometricChecking, setBiometricChecking] = useState(false);
  const backgroundAtRef = useRef<number | null>(null);
  const skipAutoLockUntilRef = useRef(0);
  const autoBiometricAttemptedRef = useRef(false);
  const biometricPromptRef = useRef(false);
  const nativeInteractionRef = useRef(false);
  const nativeInteractionReleaseTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  function applySettings(next: LocalLockSettings, initial = false) {
    const previous = settingsRef.current;
    settingsRef.current = next;
    setSettings(next);

    if (!next.enabled || (next.method === "pin" && !next.hasPin)) {
      setLocked(false);
      setError("");
      autoBiometricAttemptedRef.current = false;
      return;
    }

    if (initial) {
      setUnlockMethod("");
      setLocked(true);
      autoBiometricAttemptedRef.current = false;
      return;
    }

    // Like fAInance, activating protection from Settings must not immediately
    // cover the Settings page with the lock screen.
    if (previous && !previous.enabled && next.enabled) {
      skipAutoLockUntilRef.current = Date.now() + 5000;
      setLocked(false);
      autoBiometricAttemptedRef.current = false;
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    void getLocalLockSettings().then((next) => {
      if (!mountedRef.current) return;
      applySettings(next, true);
      setChecking(false);
    });

    const refresh = () => {
      void getLocalLockSettings().then((next) => {
        if (mountedRef.current) applySettings(next, false);
      });
    };
    window.addEventListener(LOCAL_LOCK_SETTINGS_EVENT, refresh);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(LOCAL_LOCK_SETTINGS_EVENT, refresh);
    };
  }, []);

  function lockApp() {
    const current = settingsRef.current;
    if (!current?.enabled) return;
    if (current.method === "pin" && !current.hasPin) return;
    setPin("");
    setPassword("");
    setUnlockMethod("");
    setError("");
    autoBiometricAttemptedRef.current = false;
    setLocked(true);
  }

  useEffect(() => {
    function beginNativeInteraction(event: Event) {
      const target = event.target as HTMLElement | null;
      if (!(target instanceof HTMLInputElement) || target.type !== "file") return;
      nativeInteractionRef.current = true;
      backgroundAtRef.current = null;
      skipAutoLockUntilRef.current = Date.now() + 15000;
      if (nativeInteractionReleaseTimerRef.current !== null) window.clearTimeout(nativeInteractionReleaseTimerRef.current);
    }

    function finishNativeInteraction() {
      if (!nativeInteractionRef.current) return;
      if (nativeInteractionReleaseTimerRef.current !== null) window.clearTimeout(nativeInteractionReleaseTimerRef.current);
      nativeInteractionReleaseTimerRef.current = window.setTimeout(() => {
        nativeInteractionRef.current = false;
        backgroundAtRef.current = null;
        skipAutoLockUntilRef.current = Date.now() + 1800;
        nativeInteractionReleaseTimerRef.current = null;
      }, 350);
    }

    function finishAfterFocus() {
      if (!nativeInteractionRef.current) return;
      window.setTimeout(finishNativeInteraction, 250);
    }

    document.addEventListener("click", beginNativeInteraction, true);
    document.addEventListener("change", finishNativeInteraction, true);
    document.addEventListener("cancel", finishNativeInteraction, true);
    window.addEventListener("focus", finishAfterFocus);
    return () => {
      document.removeEventListener("click", beginNativeInteraction, true);
      document.removeEventListener("change", finishNativeInteraction, true);
      document.removeEventListener("cancel", finishNativeInteraction, true);
      window.removeEventListener("focus", finishAfterFocus);
      if (nativeInteractionReleaseTimerRef.current !== null) window.clearTimeout(nativeInteractionReleaseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    let removeListener: (() => Promise<void>) | undefined;

    void App.addListener("appStateChange", ({ isActive }) => {
      if (disposed) return;
      if (!isActive) {
        if (nativeInteractionRef.current || biometricPromptRef.current || isBiometricPromptInFlight()) {
          backgroundAtRef.current = null;
          return;
        }
        backgroundAtRef.current = Date.now();
        return;
      }

      const current = settingsRef.current;
      if (!current?.enabled) return;
      if (nativeInteractionRef.current) {
        backgroundAtRef.current = null;
        skipAutoLockUntilRef.current = Date.now() + 1800;
        return;
      }
      if (biometricPromptRef.current || isBiometricPromptInFlight()) {
        backgroundAtRef.current = null;
        return;
      }
      if (Date.now() < skipAutoLockUntilRef.current) return;

      const backgroundAt = backgroundAtRef.current;
      backgroundAtRef.current = null;
      if (!backgroundAt) return;

      const elapsed = Date.now() - backgroundAt;
      if (current.timeoutMinutes <= 0 || elapsed >= current.timeoutMinutes * 60 * 1000) lockApp();
    }).then((handle) => {
      removeListener = () => handle.remove();
    }).catch(() => undefined);

    return () => {
      disposed = true;
      void removeListener?.();
    };
  }, []);

  async function biometricUnlock(reason = t("security.lockBody")) {
    if (biometricPromptRef.current) return;
    biometricPromptRef.current = true;
    setBiometricChecking(true);
    setError("");

    try {
      const result = await authenticateBiometricDetailed(reason);
      if (!mountedRef.current) return;
      if (result.ok) {
        setUnlockMethod("");
        setLocked(false);
        skipAutoLockUntilRef.current = Date.now() + 3000;
        backgroundAtRef.current = null;
      } else {
        setLocked(true);
        setError(t(biometricMessageKey(result)));
      }
    } finally {
      biometricPromptRef.current = false;
      if (mountedRef.current) setBiometricChecking(false);
    }
  }

  useEffect(() => {
    if (!locked || checking || !settings || settings.method !== "biometric" || !isNative()) return;
    if (autoBiometricAttemptedRef.current || biometricPromptRef.current || isBiometricPromptInFlight()) return;
    autoBiometricAttemptedRef.current = true;
    const timer = window.setTimeout(() => void biometricUnlock(t("security.lockBody")), 80);
    return () => window.clearTimeout(timer);
  }, [locked, checking, settings?.method]);

  async function pinUnlock() {
    setError("");
    if (!settings?.hasPin) {
      setUnlockMethod("biometric");
      setError(t("security.pinNotConfigured"));
      return;
    }
    if (await verifyPin(pin)) {
      setPin("");
      setUnlockMethod("");
      setLocked(false);
      skipAutoLockUntilRef.current = Date.now() + 3000;
    } else {
      setError(t("security.pinInvalid"));
    }
  }

  async function passwordUnlock() {
    setError("");
    setBiometricChecking(true);
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        setError(t("security.passwordUnavailable"));
        return;
      }
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      setPassword("");
      setUnlockMethod("");
      setLocked(false);
      skipAutoLockUntilRef.current = Date.now() + 3000;
    } catch {
      setError(t("security.passwordInvalid"));
    } finally {
      setBiometricChecking(false);
    }
  }

  function chooseUnlockMethod(next: LocalLockMethod) {
    setUnlockMethod(next);
    setError("");
    setPin("");
    setPassword("");
  }

  if (checking) return <main className="loading-screen"><BrandLogo compact /></main>;
  if (!settings?.enabled || !locked) return <>{children}</>;

  let activeMethod: LocalLockMethod = unlockMethod || settings.method;
  if (activeMethod === "pin" && !settings.hasPin) activeMethod = "biometric";

  return (
    <main className="lock-shell">
      <section className="lock-card">
        <div className="lock-icon" aria-hidden="true">🔐</div>
        <BrandLogo />
        <div className="lock-heading">
          <h1>{t("security.lockedTitle")}</h1>
          <p>{t("security.lockBody")}</p>
        </div>

        {error && <div className="lock-error">{error}</div>}

        <div className="lock-methods">
          <button type="button" className={activeMethod === "biometric" ? "lock-method-option active" : "lock-method-option"} onClick={() => chooseUnlockMethod("biometric")}>{t("settings.biometric")}</button>
          <button type="button" className={activeMethod === "password" ? "lock-method-option active" : "lock-method-option"} onClick={() => chooseUnlockMethod("password")}>{t("settings.passwordMethod")}</button>
          {settings.hasPin && <button type="button" className={activeMethod === "pin" ? "lock-method-option active" : "lock-method-option"} onClick={() => chooseUnlockMethod("pin")}>{t("settings.pin")}</button>}
        </div>

        {activeMethod === "pin" && (
          <div className="form-stack">
            <input
              className="input lock-pin-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              maxLength={4}
              value={pin}
              placeholder={t("security.pinPlaceholder")}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(event) => { if (event.key === "Enter") void pinUnlock(); }}
            />
            <button className="button primary" disabled={pin.length !== 4} onClick={() => void pinUnlock()}>{t("security.unlockPin")}</button>
            <button type="button" className="link-button lock-alternate" onClick={() => chooseUnlockMethod("biometric")}>{t("security.forgotPin")}</button>
          </div>
        )}

        {activeMethod === "password" && (
          <div className="form-stack">
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              placeholder={t("security.passwordPlaceholder")}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter") void passwordUnlock(); }}
            />
            <button className="button primary" disabled={biometricChecking || !password} onClick={() => void passwordUnlock()}>{biometricChecking ? t("security.biometricChecking") : t("security.unlockPassword")}</button>
          </div>
        )}

        {activeMethod === "biometric" && (
          <button className="button primary" disabled={biometricChecking} onClick={() => void biometricUnlock(t("security.lockBody"))}>
            {biometricChecking ? t("security.biometricChecking") : t("security.unlockBiometric")}
          </button>
        )}

        <p className="lock-help">{t("security.lockHelp")}</p>
      </section>
    </main>
  );
}
